import React from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import EditRestaurantCommunities from '../components/EditRestaurantCommunities'
import EditSlotRules from '../components/EditSlotRules'
import EditRestaurantBaseInfo from '../components/EditRestaurantBaseInfo'
import EditDeliverySlots from '../components/EditDeliverySlots'
import MenuHistory from '../components/MenuHistory'
import OnboardingGuide from '../components/OnboardingGuide'
import Card from '../components/Card'
import IconButton from '../components/IconButton'
import Txt from '../components/Txt'
import { renderGewerbeauskunftDialog } from '../components/GewerbeauskunftDialog'
import RestaurantActivity from '../components/RestaurantActivity'
import SelectField from '../components/SelectField'
import { APP_BAR_HEIGHT_REM } from '../components/AppBar'
import { RESTAURANT_HEADER_HEIGHT_REM, RESTAURANT_HEADER_ZINDEX } from '../consts'
import { useNavigate } from 'react-router-dom'
import { STATISTICS_PATH } from '../routes'
import { useView } from '../utils/viewContext'
import { useCurrentUser } from '../utils/user'
import { useOnboardingState } from '../utils/onboardingSteps'

const TABS = [
	{ label: 'Preview', value: 'PREVIEW', rootOnly: true },
	{ label: 'Onboarding', value: 'ONBOARDING', rootOnly: true },
	{ label: 'Menüverwaltung', value: 'MENU', rootOnly: false },
	{ label: 'Basisdaten', value: 'BASE_INFO', rootOnly: false },
	{ label: 'Status', value: 'ACTIVITY', rootOnly: false },
	{ label: 'Gemeinde', value: 'COMMUNITIES', rootOnly: true },
	{ label: 'Lieferzeitpunkte', value: 'DELIVERY_SLOTS', rootOnly: true },
] as const

// Restaurant-Auswahl und Tabs stehen in einer Zeile und bleiben unter der AppBar stehen.
const sxHeader = {
	position: 'sticky',
	top: `${APP_BAR_HEIGHT_REM}rem`,
	zIndex: RESTAURANT_HEADER_ZINDEX,
	height: `${RESTAURANT_HEADER_HEIGHT_REM}rem`,
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: '1.5rem',
	width: '100%',
	py: '0.5rem',
	bgcolor: (theme: Theme) => theme.customColors.lightBackground,
	borderBottom: 1,
	borderColor: 'divider',
} as const

const sxOnboardingCard = { p: '1.5rem', flexDirection: 'column', gap: '0.5rem' } as const

const sxSelect = {
	display: 'flex',
	alignItems: 'center',
	gap: '0.5rem',
	width: '100%',
	maxWidth: '23rem',
	flexShrink: 0,
} as const

const sxTabs = { flex: 1, minWidth: 0 } as const
// Die Fläche unter der Tab-Linie ist für jeden Tab dieselbe durchgehend weiße Karte — die Inhalte
// sitzen darauf und tragen deshalb nur eine Umrandung statt eines Schattens (siehe Card.tsx).
const sxTabPanel = { bgcolor: 'common.white', p: '1.5rem', minHeight: '20rem' } as const

type Props = {
	restaurantItems: { label: string; value: string }[]
	activeRestaurantId: string | null
	onChangeRestaurant: (value: string | null) => void
}

const RestaurantView = ({
	restaurantItems,
	activeRestaurantId,
	onChangeRestaurant,
}: Props): JSX.Element | null => {
	const { data: currentUser } = useCurrentUser()
	const { selectedRestaurantId } = useView()
	const [tab, setTab] = React.useState<string>(TABS[0].value)

	const adminRestaurants = currentUser?.adminRestaurants
	const isRoot = currentUser?.isRoot

	const restaurant = React.useMemo(
		() =>
			!selectedRestaurantId
				? null
				: adminRestaurants?.find((r) => r.id === selectedRestaurantId) ?? null,
		[adminRestaurants, selectedRestaurantId]
	)

	const communities = React.useMemo(() => restaurant?.communities ?? [], [restaurant])
	const slots = React.useMemo(() => restaurant?.slots ?? [], [restaurant])
	const deliverySlots = React.useMemo(() => restaurant?.deliverySlots ?? [], [restaurant])

	const visibleTabs = React.useMemo(() => TABS.filter((item) => isRoot || !item.rootOnly), [isRoot])

	const tabElements = React.useMemo(
		() => visibleTabs.map((item) => <Tab key={item.value} value={item.value} label={item.label} />),
		[visibleTabs]
	)

	const handleChangeTab = React.useCallback((_event: React.SyntheticEvent, value: string) => {
		setTab(value)
	}, [])

	const handleOpenGewerbeauskunft = React.useCallback(() => {
		if (restaurant) renderGewerbeauskunftDialog(restaurant)
	}, [restaurant])

	// Springt zum Eintrag in der Erhebung; die Statistik wählt den Slug aus dem Router-State aus.
	const navigate = useNavigate()
	const handleOpenStatistics = React.useCallback(() => {
		if (restaurant?.slug) navigate(STATISTICS_PATH, { state: restaurant.slug })
	}, [navigate, restaurant])

	// Ein Tab, den der aktuelle Nutzer nicht sehen darf, faellt auf den ersten sichtbaren zurueck —
	// erst wenn feststeht, wer der Nutzer ist, sonst landet auch ein Root auf dem Nicht-Root-Tab.
	React.useEffect(() => {
		if (currentUser && !visibleTabs.some((item) => item.value === tab)) setTab(visibleTabs[0].value)
	}, [currentUser, tab, visibleTabs])

	// Ein Restaurant-Admin sieht bis zum Abschluss des Onboardings nur einen Hinweis, ohne Tabs.
	const { completed: onboardingCompleted, loading: onboardingLoading } =
		useOnboardingState(restaurant)
	const showOnboarding = !isRoot && !onboardingCompleted

	return !restaurant ? null : (
		<Box width="100%">
			{/* Ohne Auswahl und ohne Tabs bliebe nur ein leerer Streifen stehen — und der klebte an
			derselben Stelle wie die Kopfzeile der Menübestätigung darunter. */}
			{isRoot || !showOnboarding ? (
				<Box sx={sxHeader}>
					{/* Nur der Root wählt hier: seine Liste trägt alle Restaurants. Der Restaurant-Admin
					sieht seines oben in der AppBar. */}
					{isRoot ? (
						<Box sx={sxSelect}>
							{/* Das Feld hält seinen Wert selbst — der Key mountet es neu, wenn die Auswahl von
							aussen wechselt. */}
							<SelectField
								key={activeRestaurantId}
								label="Restaurant"
								width="100%"
								items={restaurantItems}
								defaultValue={activeRestaurantId}
								onChange={onChangeRestaurant}
							/>
							<IconButton
								iconName="AccountBalanceOutlined"
								toolTip="Gewerberegisterauskunft beim Amt anfragen"
								onClick={handleOpenGewerbeauskunft}
							/>
							{restaurant.slug ? (
								<IconButton
									iconName="Map"
									toolTip="In der Statistik öffnen"
									onClick={handleOpenStatistics}
								/>
							) : null}
						</Box>
					) : null}
					{showOnboarding ? null : (
						<Tabs
							value={tab}
							onChange={handleChangeTab}
							variant="scrollable"
							scrollButtons="auto"
							sx={sxTabs}
						>
							{tabElements}
						</Tabs>
					)}
				</Box>
			) : null}
			{showOnboarding ? (
				onboardingLoading ? null : (
					<Card sx={sxOnboardingCard}>
						<Txt variant="h6" fontWeight="bold">
							Das Onboarding ist noch nicht abgeschlossen
						</Txt>
						<Txt variant="body2" color="text.secondary">
							Sobald Mitbestellt dein Restaurant fertig eingerichtet hat, siehst du hier dein
							Dashboard.
						</Txt>
					</Card>
				)
			) : (
				<Box sx={sxTabPanel}>
					{tab === 'BASE_INFO' ? (
						<EditRestaurantBaseInfo restaurant={restaurant} isRoot={isRoot} />
					) : tab === 'ACTIVITY' ? (
						<RestaurantActivity restaurant={restaurant} />
					) : tab === 'MENU' ? (
						<MenuHistory />
					) : tab === 'ONBOARDING' ? (
						// Die Formulare im Guide halten ihre Werte selbst — der Key mountet sie neu, damit
						// nach einem Restaurantwechsel nicht die Daten des vorherigen darin stehen.
						<OnboardingGuide key={restaurant.id} restaurant={restaurant} />
					) : tab === 'PREVIEW' ? (
						// Der reduzierte Guide: Die Karte geht ohne Vertrag online, Bestellungen werden
						// zurückgehalten, bis im Onboarding die Preview endet.
						<OnboardingGuide key={`${restaurant.id}-preview`} restaurant={restaurant} preview />
					) : tab === 'COMMUNITIES' ? (
						<EditRestaurantCommunities communities={communities} restaurantId={restaurant.id} />
					) : tab === 'DELIVERY_SLOTS' ? (
						<>
							<EditSlotRules slots={slots} restaurantId={restaurant.id} />
							<EditDeliverySlots deliverySlots={deliverySlots} communities={communities} />
						</>
					) : null}
				</Box>
			)}
		</Box>
	)
}

export default RestaurantView
